import type {
  SendTemplateComponent,
  WhatsAppTemplate,
} from '@/Domain/api/WhatsAppTemplates/WhatsAppTemplates.api';

/**
 * Extract unique {{param}} placeholders from template text, sorted.
 */
export const parseParams = (text: string): string[] => {
  const matches = text.match(/\{\{\w+\}\}/g);
  return matches ? [...new Set(matches)].sort() : [];
};

/**
 * Get the BODY component text from a template, or undefined if none.
 */
export const getBodyText = (template: WhatsAppTemplate): string | undefined => {
  const bodyComponent = template.components.find((c) => c.type === 'BODY');
  return bodyComponent?.text;
};

/**
 * Get display text: body > header > footer > name.
 */
export const getTemplateText = (template: WhatsAppTemplate): string => {
  const bodyComponent = template.components.find((c) => c.type === 'BODY');
  if (bodyComponent?.text) return bodyComponent.text;

  const headerComponent = template.components.find((c) => c.type === 'HEADER');
  if (headerComponent?.text) return headerComponent.text;

  const footerComponent = template.components.find((c) => c.type === 'FOOTER');
  if (footerComponent?.text) return footerComponent.text;

  return template.name;
};

/**
 * Replace {{param}} placeholders in text with values from paramValues.
 */
export const applyParamValues = (
  text: string,
  params: string[],
  paramValues: Record<string, string>,
): string => {
  let result = text;
  for (const param of params) {
    const value = paramValues[param]?.trim();
    if (value) {
      result = result.replaceAll(param, value);
    }
  }
  return result;
};

/**
 * Build SendTemplateComponent[] including media headers and body params.
 */
export const buildTemplateComponents = (
  template: WhatsAppTemplate,
  paramValues: Record<string, string>,
): SendTemplateComponent[] => {
  const components: SendTemplateComponent[] = [];

  const headerComponent = template.components.find((c) => c.type === 'HEADER');
  if (headerComponent?.format && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComponent.format)) {
    const headerHandle = headerComponent.example?.header_handle;
    const headerUrl = Array.isArray(headerHandle) ? headerHandle[0] : undefined;
    if (typeof headerUrl === 'string') {
      const mediaType = headerComponent.format.toLowerCase() as 'image' | 'video' | 'document';
      components.push({
        type: 'header',
        parameters: [
          {
            type: mediaType,
            [mediaType]: { link: headerUrl },
          },
        ],
      });
    }
  }

  const bodyText = getBodyText(template);
  const params = bodyText ? parseParams(bodyText) : [];

  if (params.length > 0) {
    components.push({
      type: 'body',
      parameters: params.map((param) => {
        const paramName = param.replace(/\{\{|\}\}/g, '');
        const isNamed = !/^\d+$/.test(paramName);
        return {
          type: 'text' as const,
          text: paramValues[param]?.trim() || '',
          ...(isNamed ? { parameter_name: paramName } : {}),
        };
      }),
    });
  }

  return components;
};
